import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiCalendar,
  FiUser,
  FiEye,
  FiInfo,
  FiLoader,
  FiSearch,
  FiArrowRight,
  FiActivity,
  FiAward,
} from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import api from '../api/client';
import { useApi } from '../hooks/useApi';
import { MasterTrader } from '../types';

interface ProjectionResult {
  master: { _id: string; name: string; profileImage?: string };
  input: { investmentAmount: number; days: number };
  historical: {
    totalTrades: number;
    winTrades: number;
    lossTrades: number;
    winRate: number;
    totalPnl: number;
    accountBalance: number;
    roiPercent: number;
    periodDays: number;
    dailyRoiPercent: number;
    firstTradeAt: string | null;
  };
  projection: {
    roiPercent: number;
    estimatedProfit: number;
    estimatedValue: number;
    isProfit: boolean;
  };
  disclaimer: string;
}

const AMOUNT_PRESETS = [500, 1000, 2500, 5000, 10000, 25000];
const DAY_PRESETS = [7, 14, 30, 60, 90, 180, 365];

interface CalculatorPageProps {
  selectedBrokerId?: string | null;
}

export const CalculatorPage: React.FC<CalculatorPageProps> = ({ selectedBrokerId }) => {
  const navigate = useNavigate();

  // Fetch all master traders
  const { data: mastersData, isLoading: isLoadingMasters } = useApi.useMasters(selectedBrokerId, {
    enabled: true,
  });
  const masters = useMemo(() => mastersData?.masters || [], [mastersData]);

  // Input states
  const [selectedMasterId, setSelectedMasterId] = useState<string>('');
  const [investment, setInvestment] = useState<string>('1000');
  const [days, setDays] = useState<number>(30);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Auto-select first master trader when list loads if none selected
  useEffect(() => {
    if (masters.length > 0 && (!selectedMasterId || !masters.some(m => m._id === selectedMasterId))) {
      setSelectedMasterId(masters[0]._id);
    }
  }, [masters, selectedMasterId]);

  const selectedMaster = useMemo(
    () => masters.find((m) => m._id === selectedMasterId) || masters[0] || null,
    [masters, selectedMasterId]
  );

  const numericInvestment = Math.max(0, Number(investment) || 0);
  const safeDays = Math.max(1, Number(days) || 1);

  // Query server projection for selected master
  const {
    data: projectionData,
    isFetching: isCalculating,
    error: projectionError,
  } = useQuery<{ success: boolean } & ProjectionResult>({
    queryKey: ['profitCalculatorPage', selectedMasterId, numericInvestment, safeDays],
    queryFn: async () => {
      if (!selectedMasterId) return null;
      const response = await api.get(`/master-details/${selectedMasterId}/profit-calculator`, {
        params: { investment: numericInvestment, days: safeDays },
      });
      return response.data;
    },
    enabled: !!selectedMasterId && numericInvestment >= 0 && safeDays > 0,
    staleTime: 30000,
  });

  // Filtered masters for comparison section
  const filteredMasters = useMemo(() => {
    if (!searchTerm.trim()) return masters;
    const term = searchTerm.toLowerCase();
    return masters.filter(
      (m) =>
        m.name.toLowerCase().includes(term) ||
        (m.bio && m.bio.toLowerCase().includes(term))
    );
  }, [masters, searchTerm]);

  // Client-side helper for quick comparison projections across all masters
  const getMasterQuickProjection = (master: MasterTrader) => {
    const totalTrades = master.totalTrades || 0;
    const successRate = master.successRate || 0;

    // Estimate daily return based on stats
    // Default period assumption if not available: 90 days
    const periodDays = 90;
    const totalPnl = (master as any).totalPnl || 0;
    const accountBalance = (master as any).accountBalance || 0;

    let historicalRoiPercent = 0;
    if (accountBalance > 0) {
      historicalRoiPercent = (totalPnl / accountBalance) * 100;
    } else if (successRate > 0) {
      // Fallback estimate based on win rate
      historicalRoiPercent = (successRate - 50) * 0.8;
    }

    const dailyRoiPercent = totalTrades > 0 ? historicalRoiPercent / periodDays : 0;
    const projectedRoiPercent = dailyRoiPercent * safeDays;
    const projectedProfit = numericInvestment * (projectedRoiPercent / 100);
    const projectedValue = numericInvestment + projectedProfit;

    return {
      projectedRoiPercent: Number(projectedRoiPercent.toFixed(2)),
      estimatedProfit: Math.round(projectedProfit * 100) / 100,
      estimatedValue: Math.round(projectedValue * 100) / 100,
      isProfit: projectedProfit >= 0,
    };
  };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-12 min-w-0 w-full">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white p-4 sm:p-8 shadow-xl border border-white/10">
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-300 text-xs font-semibold uppercase tracking-wider mb-3">
            <FiTrendingUp className="h-3.5 w-3.5" />
            Investment Returns Simulator
          </div>
          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white">
            Profit & Loss Calculator
          </h1>
          <p className="mt-2 text-slate-300 text-sm sm:text-base leading-relaxed">
            Select any Master Trader, enter your intended investment amount and time horizon to automatically calculate your estimated returns based on historical performance.
          </p>
        </div>
        <div className="absolute right-0 bottom-0 top-0 w-1/3 opacity-10 pointer-events-none flex items-center justify-center">
          <FiTrendingUp className="w-40 h-40 sm:w-80 sm:h-80 text-white" />
        </div>
      </div>

      {isLoadingMasters ? (
        <div className="flex flex-col items-center justify-center py-16 bg-surface border border-border rounded-2xl">
          <FiLoader className="h-8 w-8 text-primary animate-spin mb-3" />
          <p className="text-muted text-sm">Loading master traders...</p>
        </div>
      ) : masters.length === 0 ? (
        <div className="bg-surface border border-border rounded-2xl p-8 text-center">
          <FiUser className="h-12 w-12 text-muted mx-auto mb-3" />
          <h3 className="text-lg font-bold text-foreground">No Master Traders Available</h3>
          <p className="text-muted text-sm mt-1">
            There are currently no active master traders available to calculate projections.
          </p>
        </div>
      ) : (
        <>
          {/* Main Interactive Calculator Form */}
          <div className="bg-surface border border-border rounded-2xl p-4 sm:p-8 shadow-sm space-y-6">
            <h2 className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
              <FiDollarSign className="h-5 w-5 text-primary" />
              Configure Calculator Inputs
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Step 1: Select Master Trader */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-foreground">
                  1. Select Master Trader
                </label>
                <div className="relative">
                  <select
                    value={selectedMasterId}
                    onChange={(e) => setSelectedMasterId(e.target.value)}
                    className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer appearance-none"
                  >
                    {masters.map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-muted">
                    ▼
                  </div>
                </div>

                {/* Master quick info badge */}
                {selectedMaster && (
                  <div
                    onClick={() => navigate(`/master-details/${selectedMaster._id}`)}
                    className="flex items-center gap-3 p-3 bg-surface-elevated border border-border rounded-xl hover:border-primary/50 cursor-pointer transition-colors mt-3"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {selectedMaster.profileImage ? (
                        <img
                          src={selectedMaster.profileImage}
                          alt={selectedMaster.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FiUser className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-foreground truncate">{selectedMaster.name}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted">
                        <span>Win Rate: <strong className="text-emerald-500">{selectedMaster.successRate || 0}%</strong></span>
                        <span>Trades: <strong className="text-foreground">{selectedMaster.totalTrades || 0}</strong></span>
                      </div>
                    </div>
                    <FiEye className="h-4 w-4 text-muted flex-shrink-0" />
                  </div>
                )}
              </div>

              {/* Step 2: Investment Amount */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-foreground">
                  2. Investment Amount ($)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted font-bold">$</span>
                  <input
                    type="number"
                    min={1}
                    value={investment}
                    onChange={(e) => setInvestment(e.target.value)}
                    placeholder="1000"
                    className="w-full pl-9 pr-4 py-3 bg-background border border-border rounded-xl text-foreground font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                {/* Preset Chips */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {AMOUNT_PRESETS.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setInvestment(amt.toString())}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-all ${numericInvestment === amt
                          ? 'bg-primary text-white border-primary shadow-sm'
                          : 'bg-background text-muted border-border hover:border-primary/40 hover:text-foreground'
                        }`}
                    >
                      ${amt.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3: Duration (Days) */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-foreground">
                  3. Time Horizon (Days)
                </label>
                <div className="relative">
                  <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-muted h-4 w-4" />
                  <input
                    type="number"
                    min={1}
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value) || 1)}
                    placeholder="30"
                    className="w-full pl-11 pr-4 py-3 bg-background border border-border rounded-xl text-foreground font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                {/* Preset Chips */}
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {DAY_PRESETS.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDays(d)}
                      className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-all ${safeDays === d
                          ? 'bg-primary text-white border-primary shadow-sm'
                          : 'bg-background text-muted border-border hover:border-primary/40 hover:text-foreground'
                        }`}
                    >
                      {d === 365 ? '1 Year' : `${d} Days`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Hero Calculation Result Card */}
          <div className="bg-surface border border-border rounded-2xl p-4 sm:p-8 shadow-md">
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-border pb-4 mb-6">
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2 break-anywhere">
                  <FiActivity className="h-5 w-5 text-primary" />
                  Projection Results for {selectedMaster?.name || 'Master Trader'}
                </h3>
                <p className="text-xs sm:text-sm text-muted mt-0.5">
                  Calculated projection based on continuous historical trade execution.
                </p>
              </div>

              {selectedMaster && (
                <button
                  type="button"
                  onClick={() => navigate(`/master-details/${selectedMaster._id}`)}
                  className="px-4 py-2 shrink-0 bg-primary hover:bg-primary-hover text-white text-sm font-semibold rounded-xl flex items-center gap-2 transition-colors shadow-sm"
                >
                  Start Copying
                  <FiArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>

            {isCalculating ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FiLoader className="h-8 w-8 text-primary animate-spin mb-3" />
                <p className="text-muted text-sm">Calculating profit & loss projection...</p>
              </div>
            ) : projectionError ? (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-600 dark:text-red-400 text-sm">
                Failed to load profit projection. Please check your inputs and try again.
              </div>
            ) : projectionData ? (
              <div className="space-y-6">
                {/* Main Profit/Loss Display Banner */}
                <div
                  className={`rounded-2xl p-4 sm:p-6 border-2 transition-all shadow-sm ${projectionData.projection.isProfit
                      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-900 dark:text-emerald-100'
                      : 'bg-rose-500/10 border-rose-500/30 text-rose-900 dark:text-rose-100'
                    }`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                      <span className="text-xs uppercase tracking-wider font-bold opacity-80">
                        Projected {projectionData.projection.isProfit ? 'Profit' : 'Loss'} ({safeDays} Days)
                      </span>
                      <div className="flex items-center gap-3 mt-1">
                        {projectionData.projection.isProfit ? (
                          <div className="w-10 h-10 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                            <FiTrendingUp className="h-6 w-6" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0">
                            <FiTrendingDown className="h-6 w-6" />
                          </div>
                        )}
                        <div className="min-w-0 break-anywhere">
                          <span
                            className={`text-2xl sm:text-5xl font-black tracking-tight ${projectionData.projection.isProfit
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-rose-600 dark:text-rose-400'
                              }`}
                          >
                            {projectionData.projection.isProfit ? '+' : ''}
                            ${projectionData.projection.estimatedProfit.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                          <span className="ml-2 sm:ml-3 text-base sm:text-lg font-bold opacity-75">
                            ({projectionData.projection.roiPercent >= 0 ? '+' : ''}
                            {projectionData.projection.roiPercent}%)
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-surface/80 border border-border/50 rounded-xl p-4 md:text-right backdrop-blur-sm">
                      <p className="text-xs text-muted uppercase font-semibold">Projected Portfolio Value</p>
                      <p className="text-xl sm:text-3xl font-extrabold text-foreground mt-0.5 break-anywhere">
                        ${projectionData.projection.estimatedValue.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                      <p className="text-xs text-muted mt-1">
                        Starting capital: ${numericInvestment.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Performance Context Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-background border border-border rounded-xl p-4 text-center">
                    <p className="text-xs text-muted font-medium uppercase tracking-wider">All-Time ROI</p>
                    <p className={`text-xl font-bold mt-1 ${projectionData.historical.roiPercent >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {projectionData.historical.roiPercent >= 0 ? '+' : ''}
                      {projectionData.historical.roiPercent}%
                    </p>
                  </div>
                  <div className="bg-background border border-border rounded-xl p-4 text-center">
                    <p className="text-xs text-muted font-medium uppercase tracking-wider">Win Rate</p>
                    <p className="text-xl font-bold text-foreground mt-1">
                      {projectionData.historical.winRate}%
                    </p>
                  </div>
                  <div className="bg-background border border-border rounded-xl p-4 text-center">
                    <p className="text-xs text-muted font-medium uppercase tracking-wider">Total Trades</p>
                    <p className="text-xl font-bold text-foreground mt-1">
                      {projectionData.historical.totalTrades}
                    </p>
                  </div>
                  <div className="bg-background border border-border rounded-xl p-4 text-center">
                    <p className="text-xs text-muted font-medium uppercase tracking-wider">Track Record</p>
                    <p className="text-xl font-bold text-foreground mt-1">
                      {projectionData.historical.periodDays} Days
                    </p>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="flex items-start gap-3 p-4 bg-background border border-border rounded-xl text-xs text-muted leading-relaxed">
                  <FiInfo className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                  <p>{projectionData.disclaimer}</p>
                </div>
              </div>
            ) : null}
          </div>

          {/* Master Comparison Table & Selector Grid */}
          <div className="bg-surface border border-border rounded-2xl p-4 sm:p-8 shadow-sm space-y-6 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
                  <FiAward className="h-5 w-5 text-primary" />
                  All Master Traders Returns Comparison
                </h3>
                <p className="text-xs sm:text-sm text-muted mt-0.5">
                  Projections across all masters for an investment of <strong>${numericInvestment.toLocaleString()}</strong> over <strong>{safeDays} days</strong>.
                </p>
              </div>

              {/* Search Bar */}
              <div className="relative w-full sm:w-64">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search master traders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
            </div>

            {/* Masters Comparison List */}
            <div className="table-scroll overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[700px]">
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-wider text-muted font-semibold bg-background/50">
                    <th className="py-3 px-4">Master Trader</th>
                    <th className="py-3 px-4 text-center">Win Rate</th>
                    <th className="py-3 px-4 text-center">Total Trades</th>
                    <th className="py-3 px-4 text-right">Projected Profit / Loss</th>
                    <th className="py-3 px-4 text-right">Projected Portfolio</th>
                    <th className="py-3 px-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-sm">
                  {filteredMasters.map((m) => {
                    const quick = getMasterQuickProjection(m);
                    const isSelected = m._id === selectedMasterId;

                    return (
                      <tr
                        key={m._id}
                        className={`transition-colors hover:bg-background/80 ${isSelected ? 'bg-primary/5 font-medium' : ''
                          }`}
                      >
                        {/* Master Trader Info */}
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {m.profileImage ? (
                                <img src={m.profileImage} alt={m.name} className="w-full h-full object-cover" />
                              ) : (
                                <FiUser className="h-4 w-4 text-primary" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-foreground">{m.name}</span>
                                {isSelected && (
                                  <span className="px-2 py-0.5 text-[10px] font-bold bg-primary text-white rounded-full">
                                    Selected
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted truncate max-w-xs">{m.bio || 'Master Trader'}</p>
                            </div>
                          </div>
                        </td>

                        {/* Win Rate */}
                        <td className="py-4 px-4 text-center font-semibold text-emerald-500">
                          {m.successRate || 0}%
                        </td>

                        {/* Total Trades */}
                        <td className="py-4 px-4 text-center text-foreground font-semibold">
                          {m.totalTrades || 0}
                        </td>

                        {/* Projected Profit / Loss */}
                        <td className="py-4 px-4 text-right font-bold">
                          <span className={quick.isProfit ? 'text-emerald-500' : 'text-rose-500'}>
                            {quick.isProfit ? '+' : ''}
                            ${quick.estimatedProfit.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                          <span className="block text-xs text-muted font-normal">
                            ({quick.projectedRoiPercent >= 0 ? '+' : ''}
                            {quick.projectedRoiPercent}%)
                          </span>
                        </td>

                        {/* Projected Portfolio Value */}
                        <td className="py-4 px-4 text-right font-extrabold text-foreground">
                          ${quick.estimatedValue.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>

                        {/* Actions */}
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedMasterId(m._id);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                              className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${isSelected
                                  ? 'bg-primary text-white border-primary shadow-sm'
                                  : 'bg-background text-foreground border-border hover:border-primary/50'
                                }`}
                            >
                              {isSelected ? 'Calculating' : 'Select'}
                            </button>
                            <button
                              type="button"
                              onClick={() => navigate(`/master-details/${m._id}`)}
                              className="px-3 py-1.5 text-xs font-semibold bg-surface-elevated text-muted hover:text-foreground border border-border rounded-lg transition-colors"
                            >
                              View Profile
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CalculatorPage;
